import React from 'react'
import type { ClientConfig } from '../types'
import { useChatStore } from '../store/chatStore'
import { ChatBubble } from './ChatBubble'
import { MessageList } from './MessageList'
import { ConversationManager } from './ConversationManager'
import styles from './ChatWidget.module.css'

export interface ChatWidgetProps {
  /**
   * Unique identifier for the client (e.g., "frank-ciglar")
   */
  clientId: string

  /**
   * Optional client configuration override
   * If not provided, will be fetched from API
   */
  config?: Partial<ClientConfig>

  /**
   * Optional container ID to mount widget into
   * If not provided, widget will render as floating bubble
   */
  containerId?: string

  /**
   * Position of floating bubble (only used if containerId not provided)
   * @default 'bottom-right'
   */
  position?: 'bottom-right' | 'bottom-left'
}

/**
 * Main chat widget component
 * Can be embedded as floating bubble or mounted to specific container
 */
export const ChatWidget: React.FC<ChatWidgetProps> = ({
  clientId,
  config,
  containerId,
  position = 'bottom-right',
}) => {
  const {
    isOpen,
    messages,
    unreadCount,
    clientConfig,
    conversationId,
    currentStep,
    botName,
    leadData,
    calendarPushCount,
    startedAt,
    toggleChat,
    setClientConfig,
    initConversation,
    handleUserInput,
    reset,
  } = useChatStore()

  // Load client config from API if not provided
  React.useEffect(() => {
    const loadClientConfig = async () => {
      if (config) {
        setClientConfig(config as ClientConfig)
        return
      }

      // Fetch from API
      try {
        const apiUrl = (import.meta as any).env?.VITE_API_URL || 'https://qihmh0pa24.execute-api.us-west-2.amazonaws.com'
        const response = await fetch(`${apiUrl}/clients/${clientId}`)

        if (response.ok) {
          const data = await response.json()
          if (data.success && data.client) {
            setClientConfig({
              clientId: data.client.clientId,
              agentName: data.client.agentName || data.client.name,
              brandColors: {
                primary: data.client.brandColor || '#0066CC',
                secondary: '#FFFFFF',
              },
              calendlyUrl: data.client.calendlyUrl,
              notificationEmail: data.client.notificationEmail || data.client.email,
              conversationMode: data.client.conversationMode, // 'freeform' for storefront
              cities: [], // These could be added to client schema later
              priceRanges: [],
            } as ClientConfig)
            return
          }
        }
      } catch (error) {
        console.warn('Failed to fetch client config, using fallback:', error)
      }

      // Fallback defaults if API fetch fails
      const isStorefront = clientId === 'hellabots-storefront'
      setClientConfig({
        clientId,
        agentName: isStorefront ? 'HellaBots Sales' : 'Your Agent',
        brandColors: {
          primary: '#0066CC',
          secondary: '#FFFFFF',
        },
        calendlyUrl: isStorefront ? '' : '',
        notificationEmail: isStorefront ? 'sales@hellabots.com' : 'agent@example.com',
        cities: [],
        priceRanges: [],
      } as ClientConfig)
    }

    loadClientConfig()
  }, [config, clientId, setClientConfig])

  // Initialize conversation on first mount if not already initialized
  React.useEffect(() => {
    if (!conversationId && clientConfig) {
      initConversation(clientId)
    }
  }, [conversationId, clientId, clientConfig, initConversation])

  // Handle starting over
  const handleStartOver = React.useCallback(() => {
    reset()
    // Re-initialize conversation with same client
    if (clientConfig) {
      initConversation(clientId)
    }
  }, [reset, clientConfig, initConversation, clientId])

  // Auto-reset completed conversations when chat opens
  React.useEffect(() => {
    if (isOpen && currentStep === 'thank_you') {
      // Conversation is complete, reset it when user opens chat again
      // Use a small delay to avoid race conditions
      const timer = setTimeout(() => {
        handleStartOver()
      }, 100)
      return () => clearTimeout(timer)
    }
    return undefined
  }, [isOpen, currentStep, handleStartOver])

  // Determine if widget should float or be embedded
  const isFloating = !containerId
  const positionClass = position === 'bottom-left' ? styles.bottomLeft : styles.bottomRight

  const primaryColor = clientConfig?.brandColors?.primary || '#0066CC'

  return (
    <div
      className={`${styles.chatWidget} ${isFloating ? styles.floating : styles.embedded} ${
        isFloating ? positionClass : ''
      }`}
      data-testid="chat-widget"
      data-client-id={clientId}
    >
      {/* Chat bubble (only in floating mode) */}
      {isFloating && !isOpen && (
        <ChatBubble
          isOpen={isOpen}
          onClick={toggleChat}
          primaryColor={primaryColor}
          unreadCount={unreadCount}
          tooltipText={`Chat with ${clientConfig?.agentName || 'us'}`}
        />
      )}

      {/* Chat panel */}
      {(isOpen || !isFloating) && (
        <div className={styles.chatPanel} data-testid="chat-panel">
          {/* Header */}
          <div className={styles.header} style={{ backgroundColor: primaryColor }}>
            <h2 className={styles.headerTitle}>
              {clientConfig?.agentName || 'Chat'}
            </h2>
            <div className={styles.headerButtons}>
              {/* Reset button - always visible */}
              <button
                className={styles.resetButton}
                onClick={handleStartOver}
                aria-label="Start over"
                title="Start new conversation"
                data-testid="reset-button"
              >
                <svg
                  width="18"
                  height="18"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M1 4V10H7M23 20V14H17M20.49 9A9 9 0 0 0 5.64 5.64L1 10M23 14L18.36 18.36A9 9 0 0 1 3.51 15"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </button>
              {isFloating && (
                <button
                  className={styles.closeButton}
                  onClick={toggleChat}
                  aria-label="Close chat"
                  data-testid="close-button"
                >
                  <svg
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M18 6L6 18M6 6L18 18"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </button>
              )}
            </div>
          </div>

          {/* Messages */}
          <MessageList messages={messages} primaryColor={primaryColor} />

          {/* Conversation Manager */}
          {conversationId && currentStep !== 'thank_you' && (
            <ConversationManager
              conversationState={{
                conversationId,
                step: currentStep,
                leadData: {
                  ...leadData,
                  calendlyUrl: clientConfig?.calendlyUrl,
                },
                botName,
                calendarPushCount,
                messages,
                startedAt: startedAt || new Date(),
              }}
              onUserInput={handleUserInput}
              primaryColor={primaryColor}
            />
          )}

          {/* Start Over Button (shown when conversation is complete) */}
          {currentStep === 'thank_you' && (
            <button
              className={styles.startOverButton}
              onClick={handleStartOver}
              data-testid="start-over-button"
            >
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M1 4V10H7M23 20V14H17M20.49 9A9 9 0 0 0 5.64 5.64L1 10M23 14L18.36 18.36A9 9 0 0 1 3.51 15"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
              Start New Conversation
            </button>
          )}
        </div>
      )}
    </div>
  )
}
